import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Brand, Menu, MENU_ITEM_CLASS } from '@truecare/ui';
import { useCurrentUser } from '../auth/use-current-user';
import { useSignOut } from '../auth/sign-out';
import { MARKETING_URL } from '../marketing-url';

interface Props {
  children: ReactNode;
  /** When true, hides the sign-out button + the user-aware header chrome.
   *  Used by /sign-in, /password-reset, etc. */
  signedOutSurface?: boolean;
}

/**
 * The standard portal layout: header (brand + nav + sign-out) on top,
 * main below. Sign-in / verify / password-reset pages use this shell
 * with `signedOutSurface` so the chrome stays minimal.
 *
 * Logo behaviour follows the standard SaaS convention: signed-in → app home
 * (`/me`, in-app navigation); signed-out surfaces (sign-in / password-reset) →
 * marketing-site home (external, derived from the host via MARKETING_URL — never
 * hardcoded, so .cc/.ca/preview all resolve correctly). Admin mirrors this
 * (signed-in operator → admin dashboard `/`).
 */
export function PortalShell({ children, signedOutSurface }: Props) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const signOut = useSignOut();

  return (
    <div className="min-h-screen flex flex-col">
      {/* WCAG 2.4.1 — bypass blocks. First focusable element; visually hidden
          until keyboard-focused, then jumps to the main content region. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-forest focus:px-4 focus:py-2 focus:font-body focus:text-sm focus:font-medium focus:text-paper focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="border-b border-border bg-cream/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-4">
          {signedOutSurface ? (
            <a
              href={MARKETING_URL}
              className="inline-flex shrink-0 items-baseline gap-1.5"
              aria-label="TrueCare™ Partners — home"
            >
              <Brand size="md" />
            </a>
          ) : (
            <Link
              to="/me"
              className="inline-flex shrink-0 items-baseline gap-1.5"
              aria-label="TrueCare™ Partners — home"
            >
              <Brand size="md" />
            </Link>
          )}

          {!signedOutSurface && user.data && (
            <Menu
              // Audit Tier-3 a11y (WCAG 2.5.3 Label in Name): the accessible
              // name must contain the trigger's VISIBLE text (the display name),
              // so voice-control users can activate it by the name they see.
              label={`${user.data.displayName || user.data.email} — account menu`}
              trigger={
                <span
                  className="max-w-[9rem] truncate sm:max-w-[12rem]"
                  title={user.data.email}
                >
                  {user.data.displayName || user.data.email}
                </span>
              }
            >
              <Link to="/profile" className={MENU_ITEM_CLASS}>
                Profile
              </Link>
              <Link to="/security" className={MENU_ITEM_CLASS}>
                Security
              </Link>
              <button
                type="button"
                className={MENU_ITEM_CLASS}
                onClick={() => {
                  // Navigate to /sign-in regardless of POST outcome.
                  // useSignOut's onError also clears local auth (Phase 14a
                  // audit H2) so the user is fully signed out client-side
                  // even if the backend call drops mid-flight.
                  const bounce = () => navigate({ to: '/sign-in' });
                  signOut.mutate(undefined, { onSuccess: bounce, onError: bounce });
                }}
              >
                Sign out
              </button>
            </Menu>
          )}
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        {children}
      </main>
    </div>
  );
}
